import { Feature } from '../../../models';
import * as L from 'leaflet';
import * as IconFactory from '../../../services/icon.factory';

import leafletRotatableImagePlugin from './rotatable-image.plugin';
leafletRotatableImagePlugin(L);

/**
 * Factory functions helping kp-map-features controller
 */
export const imageFromFeature = (
    feature: Feature,
    mutationHandler: any,
    clickHandler: any
): {
    overlay: L.ImageOverlay;
    imgRect: L.Rectangle;
    name: L.Marker;
    handles: L.Marker[];
} => {
    const bounds: L.LatLngBounds = L.polyline(
        L.GeoJSON.coordsToLatLngs(feature.geometry.coordinates) // Must switch lat lng (GeoJSON vs. Leaflet)
    ).getBounds();

    const imgRect = L.rectangle(bounds, {
        color: 'rgba(0,0,0,0.5)',
        fillColor: 'rgba(0,0,0,0)',
        dashArray: [2, 2],
        weight: 1,
        interactive: false,
        className: 'overlay',
    });

    const setRotateMarkerLocation = () => {
        rotateMarker.setLatLng([imgRect.getBounds().getSouth(), imgRect.getBounds().getEast()]);
    };

    const rotation = feature.properties && feature.properties.rotation ? feature.properties.rotation : 0;

    // todo: fix
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - rotatableImage is an extension
    const overlay: L.ImageOverlay = L.rotatableImage(
        feature.properties.url ? feature.properties.url : IconFactory.dataUrlFromIconName('locationImage'),
        imgRect.getBounds(),
        {
            rotation,
            className: 'rotatable-image',
        }
    );

    const dragMarker = new L.Marker([imgRect.getBounds().getSouth(), imgRect.getBounds().getWest()], {
        icon: IconFactory.leafletIconFromName('move'),
        draggable: true,
    });

    // NAME LABEL for the image
    const name = L.marker(overlay.getBounds().getNorthWest(), {
        icon: IconFactory.nameLabelForImage(feature),
        interactive: false,
    });

    // EDIT MODE TOOLS for the image
    const tools = L.marker(overlay.getBounds().getNorthWest(), {
        icon: IconFactory.toolForImage(feature),
        interactive: true,
    });
    tools.on('click', clickHandler);

    // Drag handlers
    dragMarker.on('drag', () => {
        const width = imgRect.getBounds().getEast() - imgRect.getBounds().getWest();
        const height = imgRect.getBounds().getNorth() - imgRect.getBounds().getSouth();
        imgRect.setBounds([
            [dragMarker.getLatLng().lat, dragMarker.getLatLng().lng],
            [dragMarker.getLatLng().lat + height, dragMarker.getLatLng().lng + width],
        ]);

        // Scale overlay to match
        overlay.setBounds(imgRect.getBounds());

        // Move elements to match
        name.setLatLng(imgRect.getBounds().getNorthWest());
        tools.setLatLng(imgRect.getBounds().getNorthWest());
        scaleMarker.setLatLng(imgRect.getBounds().getNorthEast());

        // Update the corresponding GeoJSON element
        feature.geometry.coordinates = [
            [imgRect.getBounds().getSouthWest().lng, imgRect.getBounds().getSouthWest().lat],
            [imgRect.getBounds().getNorthEast().lng, imgRect.getBounds().getNorthEast().lat],
        ];
    });
    dragMarker.on('dragend', () => {
        mutationHandler();
    });

    const rotateMarker = new L.Marker([0, 0], {
        icon: IconFactory.leafletIconFromName('rotate'),
        draggable: true,
    });

    setRotateMarkerLocation();
    rotateMarker.on('drag', (ev: any) => {
        const angle = angleFromCoordinate(ev.latlng, overlay.getBounds().getCenter());

        rotateMarker.setLatLng(overlay.getBounds().getSouthEast());
        name.setLatLng(imgRect.getBounds().getNorthWest());
        tools.setLatLng(imgRect.getBounds().getNorthWest());

        // Update rotation
        overlay.getElement().style.transform = ` rotate(${angle}deg)`;
        (name.getElement().getElementsByClassName('name')[0] as HTMLElement).style.transform = ` rotate(${angle}deg)`;
        (tools.getElement().getElementsByClassName('nav')[0] as HTMLElement).style.transform = ` rotate(${angle}deg)`;

        // todo: fix
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - rotation is an added option, options are typed
        overlay.options.rotation = angle;
        feature.properties.rotation = angle;
        // Force redraw
        overlay.setBounds(imgRect.getBounds());
    });

    rotateMarker.on('dragend', () => {
        mutationHandler();
    });

    const scaleMarker = new L.Marker([imgRect.getBounds().getNorth(), imgRect.getBounds().getEast()], {
        icon: IconFactory.leafletIconFromName('scale'),
        draggable: true,
    });

    scaleMarker.on('dragstart', () => {
        // todo: fix
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore / Save for runtime
        overlay.aspectRatio =
            Math.abs(overlay.getBounds().getEast() - overlay.getBounds().getWest()) /
            Math.abs(overlay.getBounds().getNorth() - overlay.getBounds().getSouth());
    });

    scaleMarker.on('drag', (ev: any) => {
        const minSize = 10; // m
        if (L.latLng(ev.latlng).distanceTo(imgRect.getBounds().getSouthWest()) < minSize) {
            scaleMarker.setLatLng(imgRect.getBounds().getNorthEast());
            return;
        }
        // Control / Command / shift key enables distorting the aspect ratio
        if (!ev.originalEvent.shiftKey && !ev.originalEvent.ctrlKey && !ev.originalEvent.metaKey) {
            scaleMarker.setLatLng([
                ev.oldLatLng.lat +
                    (ev.latlng.lng - ev.oldLatLng.lng) /
                        // todo: fix
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore / fetch aspectRatio from runtime
                        overlay.aspectRatio,
                scaleMarker.getLatLng().lng,
            ]);
        }
        imgRect.setBounds([
            [imgRect.getBounds().getSouth(), imgRect.getBounds().getWest()],
            [scaleMarker.getLatLng().lat, scaleMarker.getLatLng().lng],
        ]);
        overlay.setBounds(imgRect.getBounds());
        name.setLatLng(imgRect.getBounds().getNorthWest());
        tools.setLatLng(imgRect.getBounds().getNorthWest());
        setRotateMarkerLocation();
        feature.geometry.coordinates = [
            [imgRect.getBounds().getSouthWest().lng, imgRect.getBounds().getSouthWest().lat],
            [imgRect.getBounds().getNorthEast().lng, imgRect.getBounds().getNorthEast().lat],
        ];
    });
    scaleMarker.on('dragend', () => {
        mutationHandler();
    });

    return {
        overlay,
        imgRect,
        name,
        handles: [scaleMarker, dragMarker, rotateMarker, tools],
    };
};

function angleFromCoordinate(latLng1: L.LatLng, latLng2: L.LatLng) {
    // Calculate angle in degrees from two LatLng parameters
    const angleInDegrees = (Math.atan2(latLng2.lng - latLng1.lng, latLng2.lat - latLng1.lat) * 180) / Math.PI;
    return angleInDegrees;
}
